import Vue from 'vue'
import Router from 'vue-router'
import { DOCUMENT_TITLE_PREFIX } from '@/libs/constants'

import Member from '@/views/member'
import Report from '@/views/member/report'
import HeatmapReport from '@/views/member/report/HeatmapReport'
import AnalyticsReport from '@/views/member/report/AnalyticsReport'
import ReferrersReport from '@/views/member/report/ReferrersReport'
import CtaReport from '@/views/member/report/CtaReport'
import HeatmapOnly from '@/views/member/report/HeatmapOnly'
import TimeMachineReport from '@/views/member/report/TimeMachineReport'

import ABReport from '@/views/member/report/ab'
import ABList from '@/views/member/report/ab/ABList'
import ABOption from '@/views/member/report/ab/ABOption'
import ABAddOrEdit from '@/views/member/report/ab/ABAddOrEdit'
import ABAddOrEditText from '@/views/member/report/ab/ABAddOrEditText'
import ABAddOrEditTextResult from '@/views/member/report/ab/ABAddOrEditTextResult'
import ABView from '@/views/member/report/ab/ABView'

import FunnelReport from '@/views/member/report/funnel'
import FunnelList from '@/views/member/report/funnel/FunnelList'
import FunnelView from '@/views/member/report/funnel/FunnelView'
import FunnelAddOrEdit from '@/views/member/report/funnel/FunnelAddOrEdit'

import PageAddOrEdit from '@/views/member/page/PageAddOrEdit'
import Dashboard from '@/views/member/dashboard'
import PageNotFound from '@/views/PageNotFound'

import UserInfo from '@/views/member/user-info'
import ManagePlan from '@/views/member/user-info/ManagePlan'
import RequestManagement from '@/views/member/user-info/RequestManagement'
import UserManagement from '@/views/member/user-info/UserManagement'
import PageManagement from '@/views/member/user-info/PageManagement'
import MyInformation from '@/views/member/user-info/MyInformation'
import IpBlocking from '@/views/member/user-info/IpBlocking'
import DeactivateComplete from '@/views/member/user-info/DeactivateComplete'
import HeatmapRealtime from '@/components/heatmap/HeatmapRealtime.vue'
import Mine from '@/views/member/mine/index.vue'
import Jam from '@/views/member/user-info/Jam.vue'

// auth
import SignIn from '@/views/auth/SignIn'
import Font from '@/components/common/FontTest'

// dev only
import UiComponents from '@/views/UiComponents'

// lazy loading routes
const FunnelSession = () => import(/* webpackChunkName: 'funnel-session' */ '@/views/member/report/funnel/FunnelSession')

Vue.use(Router)

// todo: i18n breadcrumb
const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'home',
      redirect: {
        name: 'dashboard'
      }
    },
    {
      path: '/components',
      component: UiComponents
    },
    {
      path: '/signin',
      name: 'sign-in',
      component: SignIn,
      meta: {
        layout: 'no-header',
        guestOnly: true
      }
    },
    {
      path: '/font',
      name: 'font',
      component: Font,
      meta: {
        layout: 'no-header',
        guestOnly: true
      }
    },
    {
      path: '/deactivate',
      name: 'deactivate',
      component: DeactivateComplete,
      meta: {
        layout: 'no-header',
        guestOnly: true
      }
    },
    {
      path: '/member',
      name: 'member',
      redirect: {
        name: 'dashboard'
      },
      component: Member,
      children: [
        {
          path: '/dashboard',
          name: 'dashboard',
          component: Dashboard,
          meta: {
            layout: 'dashboard',
            title: 'Dashboard'
          }
        },
        /* URL different in Samsung
        {
          path: '/page/add',
          name: 'page-add',
          component: PageAddOrEdit,
          meta: {
            layout: 'page',
            title: 'Page Add'
          }
        },
        */
        {
          path: '/tool/registration',
          name: 'page-add',
          component: PageAddOrEdit,
          meta: {
            layout: 'page',
            title: 'Page Add'
          }
        },
        {
          path: '/tool/etc_registration',
          name: 'page-add-etc',
          component: PageAddOrEdit,
          meta: {
            layout: 'page',
            title: 'Page Add'
          }
        },
        {
          path: '/page/edit/:aid',
          name: 'page-edit',
          component: PageAddOrEdit,
          meta: {
            layout: 'page',
            title: 'Page Edit'
          }
        },
        {
          path: '/user',
          name: 'userInfo',
          component: UserInfo,
          meta: {
            layout: 'dashboard'
          },
          children: [
            {
              path: 'manageplan',
              name: 'managePlan',
              component: ManagePlan,
              meta: {
                requireMyInfoAuth: true,
                title: 'Manage Plan'
              }
            },
            {
              path: 'requestmanagement', // Samsung only
              name: 'requestManagement',
              component: RequestManagement,
              meta: {
                requireMyInfoAuth: true,
                title: 'Request Management'
              }
            },
            {
              path: 'usermanagement', // Samsung only
              name: 'userManagement',
              component: UserManagement,
              meta: {
                requireMyInfoAuth: true,
                title: 'User Management'
              }
            },
            {
              path: 'pagemanagement', // Samsung only
              name: 'pageManagement',
              component: PageManagement,
              meta: {
                requireMyInfoAuth: true,
                title: 'Page Management'
              }
            },
            {
              path: 'jam',
              name: 'jam',
              component: Jam,
              meta: {
                requireMyInfoAuth: true,
                title: 'Jam'
              }
            },
            {
              path: 'myinformation',
              name: 'myInformation',
              component: MyInformation,
              meta: {
                requireMyInfoAuth: true,
                title: 'Info'
              }
            },
            {
              path: 'ipblocking',
              name: 'ipBlocking',
              component: IpBlocking,
              meta: {
                title: 'IP Blocking'
              }
            }
          ]
        },
        {
          path: '/heatmap/:aid([a-f\\d]+)/:abAid([a-f\\d]*)',
          component: HeatmapOnly,
          name: 'heatmap-only',
          meta: {
            layout: 'empty',
            title: ''
          }
        },
        {
          path: '/report/:aid([a-f\\d]+)',
          component: Report,
          name: 'report',
          meta: {
            // base layout of all children
            layout: 'report',
            title: null // use custom title setting
          },
          children: [
            {
              path: '',
              name: 'heatmap-report',
              component: HeatmapReport,
              meta: {
                breadcrumb: {
                  depths: [':pageName', 'Reporting Heatmaps']
                }
              }
            },
            {
              path: 'analytics',
              name: 'analytics-report',
              component: AnalyticsReport,
              meta: {
                appClass: 'app_min_1440',
                breadcrumb: {
                  depths: [':pageName', 'Analytics']
                }
              }
            },
            {
              path: 'referrers',
              name: 'referrers-report',
              component: ReferrersReport,
              meta: {
                breadcrumb: {
                  depths: [':pageName', 'Comparing Referrers']
                }
              }
            },
            {
              path: 'cta',
              name: 'cta-report',
              component: CtaReport,
              meta: {
                breadcrumb: {
                  depths: [':pageName', 'Segmenting CTA'],
                  depthsAfter: [':ctaExcept']
                }
              }
            },
            {
              path: 'time-machine',
              name: 'time-machine-report',
              component: TimeMachineReport,
              meta: {
                breadcrumb: {
                  hideDateRange: true,
                  depths: [':pageName', 'Time Machine']
                }
              }
            },
            {
              path: 'ab',
              component: ABReport,
              children: [
                {
                  path: '',
                  name: 'ab.list',
                  component: ABList,
                  meta: {
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing']
                    }
                  }
                },
                {
                  path: 'option',
                  name: 'ab.option',
                  component: ABOption,
                  meta: {
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing', 'Register (Option)']
                    }
                  }
                },
                {
                  path: 'add',
                  name: 'ab.add',
                  component: ABAddOrEdit,
                  meta: {
                    appClass: 'app_min_1440',
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing', 'Register (Setting)']
                    }
                  }
                },
                {
                  path: 'edit/:abAid',
                  name: 'ab.edit',
                  component: ABAddOrEdit,
                  meta: {
                    appClass: 'app_min_1440',
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing', 'Register (Setting)']
                    }
                  }
                },
                {
                  path: 'add/text',
                  name: 'ab.add.text',
                  component: ABAddOrEditText,
                  meta: {
                    appClass: 'app_min_1440',
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing', 'Register (Setting)']
                    }
                  }
                },
                {
                  path: 'add/text/result',
                  name: 'ab.add.text.result',
                  component: ABAddOrEditTextResult,
                  props: true,
                  meta: {
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing', 'Register (Editing)']
                    }
                  }
                },
                {
                  path: 'edit/text/:abAid',
                  name: 'ab.edit.text',
                  component: ABAddOrEditText,
                  meta: {
                    appClass: 'app_min_1440',
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':pageName', '@A/B Testing', 'Register (Setting)']
                    }
                  }
                },
                {
                  path: ':abAid/:viewType?',
                  name: 'ab.view',
                  component: ABView,
                  meta: {
                    breadcrumb: {
                      depths: [':pageName', '@A/B Testing', ':abName']
                    }
                  }
                }
              ]
            },
            {
              path: 'funnel',
              component: FunnelReport,
              children: [
                {
                  path: '',
                  name: 'funnel.list',
                  component: FunnelList,
                  meta: {
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':domain', 'Funnels List']
                    }
                  }
                },
                {
                  path: 'add',
                  name: 'funnel.add',
                  component: FunnelAddOrEdit,
                  meta: {
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':domain', '@Funnels', 'Register']
                    }
                  }
                },
                {
                  path: 'edit/:fid',
                  name: 'funnel.edit',
                  component: FunnelAddOrEdit,
                  meta: {
                    breadcrumb: {
                      hideDateRange: true,
                      depths: [':domain', '@Funnels', 'Edit']
                    }
                  }
                },
                {
                  path: ':fid/sessions',
                  name: 'funnel.sessionList',
                  component: FunnelView,
                  meta: {
                    breadcrumb: {
                      depths: [':domain', '@Funnels', '@funnelName', ':funnelSessionStep']
                    }
                  }
                },
                {
                  path: ':fid',
                  name: 'funnel.view',
                  component: FunnelView,
                  meta: {
                    breadcrumb: {
                      depths: [':domain', '@Funnels', ':funnelName']
                    }
                  }
                }
              ]
            }
          ]
        },
        {
          path: '/report/funnel/stream',
          name: 'funnel.session',
          component: FunnelSession,
          meta: {
            layout: 'empty'
          }
        },
        {
          path: '/mine',
          name: 'mine',
          component: Mine,
          meta: {
            layout: 'mine',
            title: 'Mine'
          }
        }
      ]
    },
    {
      path: '/realtime-loading',
      name: 'heatmapRealtimeLoading',
      component: HeatmapRealtime,
      meta: {
        layout: 'no-header',
        guestOnly: false
      }
    },
    {
      path: '*',
      component: PageNotFound
    }
  ]
})

router.beforeEach((to, from, next) => {
  let title = DOCUMENT_TITLE_PREFIX

  const titleList = to.matched
    .map(item => item.meta.title)
    .filter(item => item !== undefined)
    .reverse()

  let pageTitle

  if (titleList.length) {
    pageTitle = titleList[0]
  }

  if (pageTitle !== null) {
    if (pageTitle) {
      title += ` - ${pageTitle}`
    }
    document.title = title
  }
  next()
})

export default router
